/** Accounting inputs already normalized by the existing balance endpoint. */
export interface BalanceLine {
  id?: number;
  type: number;
  subtype: number;
  method: number;
  chargeType: number;
  count: number;
  taxAmount: number;
  taxExcAmount: number;
  taxIncAmount: number;
  taxAmountRate?: string | number;
  settlement?: unknown;
}

export interface AggregateLine {
  id?: number;
  type?: number;
  subtype?: number | number[];
  method: number;
  chargeType: number;
  count: number;
  taxAmount: number;
  taxExcAmount: number;
  taxIncAmount: number;
  settlement?: unknown;
}

export interface BalanceSummary {
  totalTaxincAmount1Per10: number;
  totalTaxincAmount1Per8: number;
  totalTaxAmount1Per10: number;
  totalTaxAmount1Per8: number;
  totalPaymentTaxincAmountPer10: number;
  totalPaymentTaxincAmountPer8: number;
  totalPaymentTaxAmountPer10: number;
  totalPaymentTaxAmountPer8: number;
  paymentZeroAdjustmentAmount: number;
  paymentZeroAdjustmentTaxAmount: number;

  balances: BalanceLine[];
  collectedUnpaidForDisplayOnly1: BalanceLine | null;
  subtotalCollectedUnpaidForDisplayOnlyTaxincAmount1: number;
  subtotalCollectedUnpaidForDisplayOnlyTaxAmount1: number;
  subtotalCollectedUnpaidForDisplayOnlyCount1: number;
  collectedUnpaidForDisplayOnly2: BalanceLine | null;
  subtotalCollectedUnpaidForDisplayOnlyTaxincAmount2: number;
  subtotalCollectedUnpaidForDisplayOnlyTaxAmount2: number;
  subtotalCollectedUnpaidForDisplayOnlyCount2: number;
  incomeBalanceForOthersExists: boolean;
  totalTaxincAmount1: number;
  totalTaxAmount1: number;
  totalCount11: number;
  totalTaxincAmount11: number;
  totalTaxAmount11: number;
  totalCount12: number;
  totalTaxincAmount12: number;
  totalTaxAmount12: number;
  totalTaxincAmount2: number;
  totalTaxAmount2: number;
  totalTaxincAmount3Per8: number;
  totalTaxAmount3Per8: number;
  totalTaxincAmount3Per10: number;
  totalTaxAmount3Per10: number;
  totalTaxexcAmount4: number;
  totalTaxAmount4: number;
  totalExpenseTaxincAmount: number;
  totalExpenseTaxAmount: number;
  totalPaymentTaxincAmount: number;
  totalPaymentTaxAmount: number;
  aggregateDataOfPaymentAndCollectedUnpaid: AggregateLine[];
  aggregateDataOfUnpaid: AggregateLine[];
  subtotalPaymentCount1: number;
  subtotalPaymentAmount1: number;
  subtotalPaymentTaxAmount1: number;
  subtotalCollectedUnpaidCount1: number;
  subtotalCollectedUnpaidAmount1: number;
  subtotalCollectedUnpaidTaxAmount1: number;
  subtotalUnpaidCount1: number;
  subtotalUnpaidAmount1: number;
  subtotalUnpaidTaxAmount1: number;
  subtotalPaymentCount2: number;
  subtotalPaymentAmount2: number;
  subtotalPaymentTaxAmount2: number;
  subtotalCollectedUnpaidCount2: number;
  subtotalCollectedUnpaidAmount2: number;
  subtotalCollectedUnpaidTaxAmount2: number;
  subtotalUnpaidCount2: number;
  subtotalUnpaidAmount2: number;
  subtotalUnpaidTaxAmount2: number;
  totalAdvancesTaxincAmountPar8: number;
  totalAdvancesTaxAmountPar8: number;
  totalAdvancesTaxincAmountPar10: number;
  totalAdvancesTaxAmountPar10: number;
  subtotalCashPaymentCount1: number;
  subtotalCashPaymentAmount1: number;
  subtotalCashPaymentTaxAmount1: number;
  subtotalCashPaymentCount2: number;
  subtotalCashPaymentAmount2: number;
  subtotalCashPaymentTaxAmount2: number;
  subtotalCashCollectedUnpaidCount1: number;
  subtotalCashCollectedUnpaidAmount1: number;
  subtotalCashCollectedUnpaidTaxAmount1: number;
  subtotalCashCollectedUnpaidCount2: number;
  subtotalCashCollectedUnpaidAmount2: number;
  subtotalCashCollectedUnpaidTaxAmount2: number;
}

export function getTaxAmount(taxIncAmount: number, taxRate: number): number {
  // Integer percentages avoid flooring 13,200 / 1.1 * 0.1 to 1,199.
  const percent = Math.round(Number(taxRate) * 100);
  return Math.floor(Number(taxIncAmount) * percent / (100 + percent));
}

/** Preserve the existing row order, tax rounding and cash deduction sequence. Never mutate input. */
export function summarizeBalance(data: readonly BalanceLine[]): BalanceSummary {
  const result: BalanceSummary = {
    totalTaxincAmount1Per10: 0,
    totalTaxincAmount1Per8: 0,
    totalTaxAmount1Per10: 0,
    totalTaxAmount1Per8: 0,
    totalPaymentTaxincAmountPer10: 0,
    totalPaymentTaxincAmountPer8: 0,
    totalPaymentTaxAmountPer10: 0,
    totalPaymentTaxAmountPer8: 0,
    paymentZeroAdjustmentAmount: 0,
    paymentZeroAdjustmentTaxAmount: 0,

    balances: [],
    collectedUnpaidForDisplayOnly1: null,
    subtotalCollectedUnpaidForDisplayOnlyTaxincAmount1: 0,
    subtotalCollectedUnpaidForDisplayOnlyTaxAmount1: 0,
    subtotalCollectedUnpaidForDisplayOnlyCount1: 0,
    collectedUnpaidForDisplayOnly2: null,
    subtotalCollectedUnpaidForDisplayOnlyTaxincAmount2: 0,
    subtotalCollectedUnpaidForDisplayOnlyTaxAmount2: 0,
    subtotalCollectedUnpaidForDisplayOnlyCount2: 0,
    incomeBalanceForOthersExists: false,
    totalTaxincAmount1: 0,
    totalTaxAmount1: 0,
    totalCount11: 0,
    totalTaxincAmount11: 0,
    totalTaxAmount11: 0,
    totalCount12: 0,
    totalTaxincAmount12: 0,
    totalTaxAmount12: 0,
    totalTaxincAmount2: 0,
    totalTaxAmount2: 0,
    totalTaxincAmount3Per8: 0,
    totalTaxAmount3Per8: 0,
    totalTaxincAmount3Per10: 0,
    totalTaxAmount3Per10: 0,
    totalTaxexcAmount4: 0,
    totalTaxAmount4: 0,
    totalExpenseTaxincAmount: 0,
    totalExpenseTaxAmount: 0,
    totalPaymentTaxincAmount: 0,
    totalPaymentTaxAmount: 0,
    aggregateDataOfPaymentAndCollectedUnpaid: [],
    aggregateDataOfUnpaid: [],
    subtotalPaymentCount1: 0,
    subtotalPaymentAmount1: 0,
    subtotalPaymentTaxAmount1: 0,
    subtotalCollectedUnpaidCount1: 0,
    subtotalCollectedUnpaidAmount1: 0,
    subtotalCollectedUnpaidTaxAmount1: 0,
    subtotalUnpaidCount1: 0,
    subtotalUnpaidAmount1: 0,
    subtotalUnpaidTaxAmount1: 0,
    subtotalPaymentCount2: 0,
    subtotalPaymentAmount2: 0,
    subtotalPaymentTaxAmount2: 0,
    subtotalCollectedUnpaidCount2: 0,
    subtotalCollectedUnpaidAmount2: 0,
    subtotalCollectedUnpaidTaxAmount2: 0,
    subtotalUnpaidCount2: 0,
    subtotalUnpaidAmount2: 0,
    subtotalUnpaidTaxAmount2: 0,
    totalAdvancesTaxincAmountPar8: 0,
    totalAdvancesTaxAmountPar8: 0,
    totalAdvancesTaxincAmountPar10: 0,
    totalAdvancesTaxAmountPar10: 0,
    subtotalCashPaymentCount1: 0,
    subtotalCashPaymentAmount1: 0,
    subtotalCashPaymentTaxAmount1: 0,
    subtotalCashPaymentCount2: 0,
    subtotalCashPaymentAmount2: 0,
    subtotalCashPaymentTaxAmount2: 0,
    subtotalCashCollectedUnpaidCount1: 0,
    subtotalCashCollectedUnpaidAmount1: 0,
    subtotalCashCollectedUnpaidTaxAmount1: 0,
    subtotalCashCollectedUnpaidCount2: 0,
    subtotalCashCollectedUnpaidAmount2: 0,
    subtotalCashCollectedUnpaidTaxAmount2: 0,
  };
      result.balances = [...data];

      // --- Reset all computed values ---
      result.collectedUnpaidForDisplayOnly1 = null;
      result.subtotalCollectedUnpaidForDisplayOnlyTaxincAmount1 = 0;
      result.subtotalCollectedUnpaidForDisplayOnlyTaxAmount1 = 0;
      result.subtotalCollectedUnpaidForDisplayOnlyCount1 = 0;
      result.collectedUnpaidForDisplayOnly2 = null;
      result.subtotalCollectedUnpaidForDisplayOnlyTaxincAmount2 = 0;
      result.subtotalCollectedUnpaidForDisplayOnlyTaxAmount2 = 0;
      result.subtotalCollectedUnpaidForDisplayOnlyCount2 = 0;

      // Splice out type-99 display-only items
      const idx1 = result.balances.findIndex(v => v.type === 99 && v.subtype === 99 && v.method === 1);
      if (idx1 !== -1) {
        result.collectedUnpaidForDisplayOnly1 = result.balances.splice(idx1, 1)[0];
        result.subtotalCollectedUnpaidForDisplayOnlyTaxincAmount1 = result.collectedUnpaidForDisplayOnly1.taxIncAmount;
        result.subtotalCollectedUnpaidForDisplayOnlyTaxAmount1 = result.collectedUnpaidForDisplayOnly1.taxAmount;
        result.subtotalCollectedUnpaidForDisplayOnlyCount1 = result.collectedUnpaidForDisplayOnly1.count;
      }
      const idx2 = result.balances.findIndex(v => v.type === 99 && v.subtype === 99 && v.method === 2);
      if (idx2 !== -1) {
        result.collectedUnpaidForDisplayOnly2 = result.balances.splice(idx2, 1)[0];
        result.subtotalCollectedUnpaidForDisplayOnlyTaxincAmount2 = result.collectedUnpaidForDisplayOnly2.taxIncAmount;
        result.subtotalCollectedUnpaidForDisplayOnlyTaxAmount2 = result.collectedUnpaidForDisplayOnly2.taxAmount;
        result.subtotalCollectedUnpaidForDisplayOnlyCount2 = result.collectedUnpaidForDisplayOnly2.count;
      }

      result.incomeBalanceForOthersExists = !!data.find((v: BalanceLine) => v.type === 1 && v.method !== 1 && v.method !== 2);

      const balanceData: BalanceLine[] = result.balances;

      // --- Initialize totals ---
      result.totalTaxincAmount1 = 0;
      result.totalTaxAmount1 = 0;
      result.totalCount11 = 0;
      result.totalTaxincAmount11 = 0;
      result.totalTaxAmount11 = 0;
      result.totalCount12 = 0;
      result.totalTaxincAmount12 = 0;
      result.totalTaxAmount12 = 0;
      result.totalTaxincAmount2 = 0;
      result.totalTaxAmount2 = 0;
      result.totalTaxincAmount3Per8 = 0;
      result.totalTaxAmount3Per8 = 0;
      result.totalTaxincAmount3Per10 = 0;
      result.totalTaxAmount3Per10 = 0;
      result.totalTaxexcAmount4 = 0;
      result.totalTaxAmount4 = 0;
      result.totalExpenseTaxincAmount = 0;
      result.totalExpenseTaxAmount = 0;
      result.totalPaymentTaxincAmount = 0;
      result.totalPaymentTaxAmount = 0;

      // --- Aggregate payment and collected-unpaid (subtype 11 | 12) ---
      result.aggregateDataOfPaymentAndCollectedUnpaid = balanceData.reduce((r: AggregateLine[], d: BalanceLine) => {
        if (d.subtype === 11 || d.subtype === 12) {
          let target = r.find(v => v.chargeType === d.chargeType && v.method === d.method);
          if (!target) {
            target = {
              id: d.id,
              chargeType: d.chargeType,
              method: d.method,
              type: d.type,
              subtype: [11, 12, 14, 15],
              settlement: d.settlement,
              count: d.count,
              taxAmount: d.taxAmount,
              taxExcAmount: d.taxExcAmount,
              taxIncAmount: d.taxIncAmount,
            };
            r.push(target);
          } else {
            if (!target.id) {
              target.id = d.id;
              target.type = d.type;
              target.subtype = [11, 12, 14, 15];
              target.settlement = d.settlement;
            }
            target.count += d.count;
            target.taxAmount += d.taxAmount;
            target.taxExcAmount += d.taxExcAmount;
            target.taxIncAmount += d.taxIncAmount;
          }
        }
        return r;
      }, [
        { chargeType: 1, method: 1, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 1, method: 2, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 2, method: 1, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 2, method: 2, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 3, method: 1, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 3, method: 2, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 4, method: 1, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 4, method: 2, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 5, method: 1, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 5, method: 2, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 0, method: 1, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 0, method: 2, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
      ]);

      // --- Aggregate unpaid (subtype 13) ---
      result.aggregateDataOfUnpaid = balanceData.reduce((r: AggregateLine[], d: BalanceLine) => {
        if (d.subtype === 13) {
          let target = r.find(v => v.chargeType === d.chargeType && v.method === d.method);
          if (!target) {
            target = {
              id: d.id,
              chargeType: d.chargeType,
              method: d.method,
              type: d.type,
              subtype: 13,
              settlement: d.settlement,
              count: d.count,
              taxAmount: d.taxAmount,
              taxExcAmount: d.taxExcAmount,
              taxIncAmount: d.taxIncAmount,
            };
            r.push(target);
          } else {
            if (!target.id) {
              target.id = d.id;
              target.type = d.type;
              target.subtype = 13;
              target.settlement = d.settlement;
            }
            target.count += d.count;
            target.taxAmount += d.taxAmount;
            target.taxExcAmount += d.taxExcAmount;
            target.taxIncAmount += d.taxIncAmount;
          }
        }
        return r;
      }, [
        { chargeType: 1, method: 1, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 1, method: 2, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 2, method: 1, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 2, method: 2, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 3, method: 1, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 3, method: 2, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 4, method: 1, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 4, method: 2, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 5, method: 1, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 5, method: 2, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 0, method: 1, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
        { chargeType: 0, method: 2, count: 0, taxAmount: 0, taxExcAmount: 0, taxIncAmount: 0 },
      ]);

      // --- Subtotals for method=1 (bank transfer) ---
      [
        result.subtotalPaymentCount1,
        result.subtotalPaymentAmount1,
        result.subtotalPaymentTaxAmount1,
        result.subtotalCollectedUnpaidCount1,
        result.subtotalCollectedUnpaidAmount1,
        result.subtotalCollectedUnpaidTaxAmount1,
        result.subtotalUnpaidCount1,
        result.subtotalUnpaidAmount1,
        result.subtotalUnpaidTaxAmount1,
      ] = balanceData.reduce((r: number[], v: BalanceLine) => {
        if (v.method === 1) {
          if (v.subtype === 11 || v.subtype === 12) {
            r[0] += v.count;
            r[1] += v.taxIncAmount;
            r[2] += v.taxAmount;
          }
          if (v.subtype === 12) {
            r[3] += v.count;
            r[4] += v.taxIncAmount;
            r[5] += v.taxAmount;
          }
          if (v.subtype === 13) {
            r[6] += v.count;
            r[7] += v.taxIncAmount;
            r[8] += v.taxAmount;
          }
        }
        return r;
      }, [0, 0, 0, 0, 0, 0, 0, 0, 0]);

      // --- Subtotals for method=2 (credit card) ---
      [
        result.subtotalPaymentCount2,
        result.subtotalPaymentAmount2,
        result.subtotalPaymentTaxAmount2,
        result.subtotalCollectedUnpaidCount2,
        result.subtotalCollectedUnpaidAmount2,
        result.subtotalCollectedUnpaidTaxAmount2,
        result.subtotalUnpaidCount2,
        result.subtotalUnpaidAmount2,
        result.subtotalUnpaidTaxAmount2,
      ] = balanceData.reduce((r: number[], v: BalanceLine) => {
        if (v.method === 2 && (v.subtype === 11 || v.subtype === 12)) {
          r[0] += v.count;
          r[1] += v.taxIncAmount;
          r[2] += v.taxAmount;
        }
        if (v.method === 2 && v.subtype === 12) {
          r[3] += v.count;
          r[4] += v.taxIncAmount;
          r[5] += v.taxAmount;
        }
        if (v.method === 2 && v.subtype === 13) {
          r[6] += v.count;
          r[7] += v.taxIncAmount;
          r[8] += v.taxAmount;
        }
        return r;
      }, [0, 0, 0, 0, 0, 0, 0, 0, 0]);

      // --- Per-item aggregation ---
      for (const item of balanceData) {
        if (item.type === 1) {
          if (item.subtype !== 13) {
            result.totalTaxincAmount1 += item.taxIncAmount;
            if (Number(item.taxAmountRate) === 0.08) result.totalTaxincAmount1Per8 += item.taxIncAmount;
            else result.totalTaxincAmount1Per10 += item.taxIncAmount;
          }
          if (item.method === 1) {
            result.totalCount11 += item.count;
            result.totalTaxincAmount11 += item.taxIncAmount;
            result.totalTaxAmount11 += item.taxAmount;
          }
          if (item.method === 2) {
            result.totalCount12 += item.count;
            result.totalTaxincAmount12 += item.taxIncAmount;
            result.totalTaxAmount12 += item.taxAmount;
          }
        }
        if (item.type === 2) {
          result.totalTaxincAmount2 += item.taxIncAmount;
          result.totalTaxAmount2 += item.taxAmount;
        }
        if (item.type === 3) {
          if (Number(item.taxAmountRate) === 0.08) {
            result.totalTaxincAmount3Per8 += item.taxIncAmount;
          } else {
            result.totalTaxincAmount3Per10 += item.taxIncAmount;
          }
        }
        if (item.type === 4) {
          result.totalTaxexcAmount4 += item.taxExcAmount;
          result.totalTaxAmount4 += item.taxAmount;
        }
      }

      result.totalTaxAmount1Per10 = getTaxAmount(result.totalTaxincAmount1Per10, 0.1);
      result.totalTaxAmount1Per8 = getTaxAmount(result.totalTaxincAmount1Per8, 0.08);
      result.totalTaxAmount1 = result.totalTaxAmount1Per10 + result.totalTaxAmount1Per8;
      result.totalTaxAmount3Per10 = getTaxAmount(result.totalTaxincAmount3Per10, 0.1);
      result.totalTaxAmount3Per8 = getTaxAmount(result.totalTaxincAmount3Per8, 0.08);

      // --- Advances ---
      result.totalAdvancesTaxincAmountPar8 = balanceData.reduce((sum: number, d: BalanceLine) => {
        if (d.subtype === 50 && Number(d.taxAmountRate) === 0.08) sum += d.taxIncAmount;
        return sum;
      }, 0);
      result.totalAdvancesTaxAmountPar8 = getTaxAmount(result.totalAdvancesTaxincAmountPar8, 0.08);
      result.totalAdvancesTaxincAmountPar10 = balanceData.reduce((sum: number, d: BalanceLine) => {
        if (d.subtype === 50 && Number(d.taxAmountRate) === 0.1) sum += d.taxIncAmount;
        return sum;
      }, 0);
      result.totalAdvancesTaxAmountPar10 = getTaxAmount(result.totalAdvancesTaxincAmountPar10, 0.1);

      result.totalExpenseTaxincAmount = result.totalTaxincAmount3Per8 + result.totalTaxincAmount3Per10
        + result.totalTaxexcAmount4
        + result.totalAdvancesTaxincAmountPar8 + result.totalAdvancesTaxincAmountPar10;
      result.totalExpenseTaxAmount = result.totalTaxAmount3Per8 + result.totalTaxAmount3Per10
        + result.totalTaxAmount4
        + result.totalAdvancesTaxAmountPar8 + result.totalAdvancesTaxAmountPar10;
      result.totalPaymentTaxincAmount = result.totalTaxincAmount1 - result.totalExpenseTaxincAmount;
      result.totalPaymentTaxAmount = result.totalTaxAmount1 - result.totalExpenseTaxAmount;
      if (result.totalPaymentTaxincAmount < 0) {
        result.totalPaymentTaxincAmount = 0;
        result.totalPaymentTaxAmount = 0;
      }

      // --- Cash payment subtotals ---
      const cashPaymentPaymentBalances = balanceData.filter((v: BalanceLine) => v.subtype === 14);
      const cashCollectedUnpaidBalances = balanceData.filter((v: BalanceLine) => v.subtype === 15);

      [
        result.subtotalCashPaymentCount1,
        result.subtotalCashPaymentAmount1,
        result.subtotalCashPaymentTaxAmount1,
        result.subtotalCashPaymentCount2,
        result.subtotalCashPaymentAmount2,
        result.subtotalCashPaymentTaxAmount2,
      ] = cashPaymentPaymentBalances.reduce((r: number[], v: BalanceLine) => {
        if (v.method === 1) {
          r[0] += v.count; r[1] += v.taxIncAmount; r[2] += v.taxAmount;
        } else if (v.method === 2) {
          r[3] += v.count; r[4] += v.taxIncAmount; r[5] += v.taxAmount;
        }
        return r;
      }, [0, 0, 0, 0, 0, 0]);

      [
        result.subtotalCashCollectedUnpaidCount1,
        result.subtotalCashCollectedUnpaidAmount1,
        result.subtotalCashCollectedUnpaidTaxAmount1,
        result.subtotalCashCollectedUnpaidCount2,
        result.subtotalCashCollectedUnpaidAmount2,
        result.subtotalCashCollectedUnpaidTaxAmount2,
      ] = cashCollectedUnpaidBalances.reduce((r: number[], v: BalanceLine) => {
        if (v.method === 1) {
          r[0] += v.count; r[1] += v.taxIncAmount; r[2] += v.taxAmount;
        } else if (v.method === 2) {
          r[3] += v.count; r[4] += v.taxIncAmount; r[5] += v.taxAmount;
        }
        return r;
      }, [0, 0, 0, 0, 0, 0]);

      result.subtotalPaymentCount1 += result.subtotalCashPaymentCount1 + result.subtotalCashCollectedUnpaidCount1;
      result.subtotalPaymentAmount1 += result.subtotalCashPaymentAmount1 + result.subtotalCashCollectedUnpaidAmount1;
      result.subtotalPaymentTaxAmount1 += result.subtotalCashPaymentTaxAmount1 + result.subtotalCashCollectedUnpaidTaxAmount1;
      result.subtotalPaymentCount2 += result.subtotalCashPaymentCount2 + result.subtotalCashCollectedUnpaidCount2;
      result.subtotalPaymentAmount2 += result.subtotalCashPaymentAmount2 + result.subtotalCashCollectedUnpaidAmount2;
      result.subtotalPaymentTaxAmount2 += result.subtotalCashPaymentTaxAmount2 + result.subtotalCashCollectedUnpaidTaxAmount2;

      // Merge cash payments into aggregate
      const cashPaymentBalances = cashPaymentPaymentBalances.concat(cashCollectedUnpaidBalances);
      result.aggregateDataOfPaymentAndCollectedUnpaid = cashPaymentBalances.reduce((r: AggregateLine[], d: BalanceLine) => {
        let target = r.find(v => v.chargeType === d.chargeType && v.method === d.method);
        if (!target) {
          target = {
            id: d.id,
            chargeType: d.chargeType,
            method: d.method,
            type: d.type,
            subtype: [11, 12, 14, 15],
            settlement: d.settlement,
            count: d.count,
            taxAmount: d.taxAmount,
            taxExcAmount: d.taxExcAmount,
            taxIncAmount: d.taxIncAmount,
          };
          r.push(target);
        } else {
          if (!target.id) {
            target.id = d.id;
            target.type = d.type;
            target.subtype = [11, 12, 14, 15];
            target.settlement = d.settlement;
          }
          target.count += d.count;
          target.taxAmount += d.taxAmount;
          target.taxExcAmount += d.taxExcAmount;
          target.taxIncAmount += d.taxIncAmount;
        }
        return r;
      }, result.aggregateDataOfPaymentAndCollectedUnpaid);

      // Subtract cash from payment total
      const subtotalCashPaymentAmountTotal = result.subtotalCashPaymentAmount1 + result.subtotalCashPaymentAmount2;
      const subtotalCashCollectedUnpaidAmountTotal = result.subtotalCashCollectedUnpaidAmount1 + result.subtotalCashCollectedUnpaidAmount2;
      const subtotalCashPaymentTaxAmountTotal = result.subtotalCashPaymentTaxAmount1 + result.subtotalCashPaymentTaxAmount2;
      const subtotalCashCollectedUnpaidTaxAmountTotal = result.subtotalCashCollectedUnpaidTaxAmount1 + result.subtotalCashCollectedUnpaidTaxAmount2;

      result.totalPaymentTaxincAmount -= subtotalCashPaymentAmountTotal + subtotalCashCollectedUnpaidAmountTotal;
      result.totalPaymentTaxAmount -= subtotalCashPaymentTaxAmountTotal + subtotalCashCollectedUnpaidTaxAmountTotal;
      if (result.totalPaymentTaxincAmount < 0) {
        result.totalPaymentTaxincAmount = 0;
        result.totalPaymentTaxAmount = 0;
      }
  const cash = (rate: number) => cashPaymentBalances
    .filter(item => [1, 2].includes(item.method) && (Number(item.taxAmountRate) === 0.08 ? 8 : 10) === rate)
    .reduce((sum, item) => ({ amount: sum.amount + item.taxIncAmount, tax: sum.tax + item.taxAmount }), { amount: 0, tax: 0 });
  const cash10 = cash(10), cash8 = cash(8);
  result.totalPaymentTaxincAmountPer10 = result.totalTaxincAmount1Per10 - result.totalTaxincAmount3Per10 - result.totalAdvancesTaxincAmountPar10 - cash10.amount;
  result.totalPaymentTaxincAmountPer8 = result.totalTaxincAmount1Per8 - result.totalTaxincAmount3Per8 - result.totalAdvancesTaxincAmountPar8 - cash8.amount;
  result.totalPaymentTaxAmountPer10 = result.totalTaxAmount1Per10 - result.totalTaxAmount3Per10 - result.totalAdvancesTaxAmountPar10 - cash10.tax;
  result.totalPaymentTaxAmountPer8 = result.totalTaxAmount1Per8 - result.totalTaxAmount3Per8 - result.totalAdvancesTaxAmountPar8 - cash8.tax;
  result.paymentZeroAdjustmentAmount = result.totalPaymentTaxincAmount -
    (result.totalPaymentTaxincAmountPer10 + result.totalPaymentTaxincAmountPer8 - result.totalTaxexcAmount4);
  result.paymentZeroAdjustmentTaxAmount = result.totalPaymentTaxAmount -
    (result.totalPaymentTaxAmountPer10 + result.totalPaymentTaxAmountPer8 - result.totalTaxAmount4);
  return result;
}
